import { create } from "zustand";
import { persist } from "zustand/middleware";
import { trackAddToCart, trackViewCart } from "./analytics";

export type CartItem =
  | { type: "package"; id: string; slug: string; name: string; price: number; includedServiceIds: string[] }
  | { type: "service"; id: string; slug: string; name: string; price: number };

type State = {
  items: CartItem[];
  open: boolean;
};
type Actions = {
  addPackage: (p: Omit<Extract<CartItem, { type: "package" }>, "type">) => void;
  addService: (s: Omit<Extract<CartItem, { type: "service" }>, "type">) => void;
  remove: (id: string) => void;
  clear: () => void;
  setOpen: (b: boolean) => void;
};

export const useCart = create<State & Actions>()(
  persist(
    (set, get) => ({
      items: [],
      open: false,
      addPackage: (p) => {
        const items = get().items.filter((i) => i.type !== "package");
        // Remove any individual services already included in this package
        const filtered = items.filter((i) => !(i.type === "service" && p.includedServiceIds.includes(i.id)));
        set({ items: [{ type: "package", ...p }, ...filtered] });
        trackAddToCart({ id: p.id, slug: p.slug, name: p.name, price: Number(p.price), type: "package" });
      },
      addService: (s) => {
        const items = get().items;
        const pkg = items.find((i) => i.type === "package") as Extract<CartItem, { type: "package" }> | undefined;
        if (pkg && pkg.includedServiceIds.includes(s.id)) return; // already included
        if (items.some((i) => i.type === "service" && i.id === s.id)) return;
        set({ items: [...items, { type: "service", ...s }] });
        trackAddToCart({ id: s.id, slug: s.slug, name: s.name, price: Number(s.price), type: "service" });
      },
      remove: (id) => set({ items: get().items.filter((i) => i.id !== id) }),
      clear: () => set({ items: [] }),
      setOpen: (b) => {
        set({ open: b });
        if (b) {
          const current = get().items;
          const total = current.reduce((acc, i) => acc + Number(i.price), 0);
          trackViewCart(
            current.map((i) => ({
              id: i.id,
              slug: i.slug,
              name: i.name,
              price: Number(i.price),
              type: i.type,
            })),
            total
          );
        }
      },
    }),
    { name: "clf-cart-v1" }
  )
);

export function cartTotals(items: CartItem[]) {
  const subtotal = items.reduce((acc, i) => acc + Number(i.price), 0);
  return { subtotal, total: subtotal };
}

export function isIncluded(serviceId: string, items: CartItem[]) {
  const pkg = items.find((i) => i.type === "package") as Extract<CartItem, { type: "package" }> | undefined;
  return !!pkg?.includedServiceIds.includes(serviceId);
}

/** Package info for upgrade suggestions. */
export type PackageInfo = {
  id: string; slug: string; name: string; price: number;
  original_value: number; savings: number; service_ids: string[];
};

/**
 * Suggest a package upgrade. Cubre 3 escenarios:
 * 1) Sin paquete + servicios sueltos que cubren ≥50% de un paquete y el paquete es más barato.
 * 2) Con paquete inferior + extras: si el paquete superior cubre todos los extras "relevantes"
 *    (extras que están incluidos en el paquete superior) y precio_superior < precio_actual + suma_de_esos_extras.
 * 3) Si ya tiene el paquete con más servicios (Premium), nunca sugiere.
 *
 * Devuelve { pkg, saves, mode } donde mode es "from-services" o "upgrade-from-package".
 */
export function suggestPackage(
  items: CartItem[],
  packages: PackageInfo[]
): { pkg: PackageInfo; saves: number; mode: "from-services" | "upgrade-from-package" } | null {
  const currentPkg = items.find((i) => i.type === "package") as
    | Extract<CartItem, { type: "package" }>
    | undefined;
  const serviceItems = items.filter((i) => i.type === "service") as Extract<CartItem, { type: "service" }>[];

  // Caso 1: sin paquete
  if (!currentPkg) {
    if (serviceItems.length < 2) return null;
    const standaloneTotal = serviceItems.reduce((a, i) => a + Number(i.price), 0);
    let best: { pkg: PackageInfo; saves: number } | null = null;
    for (const pkg of packages) {
      if (pkg.service_ids.length === 0) continue;
      const overlap = serviceItems.filter((s) => pkg.service_ids.includes(s.id)).length;
      const coverage = overlap / pkg.service_ids.length;
      const saves = standaloneTotal - Number(pkg.price);
      if (coverage >= 0.5 && saves > 0) {
        if (!best || saves > best.saves) best = { pkg, saves };
      }
    }
    return best ? { ...best, mode: "from-services" } : null;
  }

  // Caso 2: con paquete inferior + extras → buscar paquete superior que sea superset
  // Paquete superior = uno con más service_ids que el actual y cuyo set incluye los del actual.
  const currentIncluded = new Set(currentPkg.includedServiceIds);
  const candidates = packages.filter(
    (p) =>
      p.id !== currentPkg.id &&
      p.service_ids.length > currentIncluded.size &&
      currentPkg.includedServiceIds.every((id) => p.service_ids.includes(id))
  );
  if (candidates.length === 0) return null;

  // Para cada candidato, calcular costo actual = precio paquete + suma de extras que el candidato cubre
  let bestUpgrade: { pkg: PackageInfo; saves: number } | null = null;
  for (const cand of candidates) {
    const candSet = new Set(cand.service_ids);
    const coveredExtras = serviceItems.filter((s) => candSet.has(s.id) && !currentIncluded.has(s.id));
    if (coveredExtras.length === 0) continue; // no aporta nada cambiar
    const currentCost = Number(currentPkg.price) + coveredExtras.reduce((a, i) => a + Number(i.price), 0);
    const saves = currentCost - Number(cand.price);
    if (saves > 0 && (!bestUpgrade || saves > bestUpgrade.saves)) {
      bestUpgrade = { pkg: cand, saves };
    }
  }
  return bestUpgrade ? { ...bestUpgrade, mode: "upgrade-from-package" } : null;
}
